import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LucideAngularModule, Edit, Trash2, Plus, Calendar, Clock, Users, Building } from 'lucide-angular';
import { takeUntil } from 'rxjs';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventTemplateService } from '../shared/event-template.service';
import { EventTemplateModel, EventTemplateModelUtils } from '../shared/event-template.model';
import { EventTemplateItemModel, EventTemplateItemModelUtils } from '../shared/event-template-item.model';
import { NotificationService } from '@app/shared/notification/notification.service';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { DatePipe } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { EventSelectionFormComponent } from './event-selection-form.component';
import { RightSidebarComponent } from '@app/partials/right-sidebar/right-sidebar.component';
import { EventModel } from '@app/events/shared/event.model';
import { EventService } from '@app/events/shared/event.service';
import { AuthService } from '@app/auth/auth.service';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';

@Component({
  selector: 'app-event-template-view',
  templateUrl: './event-template-view.component.html',
  imports: [LucideAngularModule, BadgeComponent, DatePipe, ButtonComponent, RightSidebarComponent, EventSelectionFormComponent, RouterLink],
  standalone: true
})
export class EventTemplateViewComponent extends Destroyer implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventTemplateService: EventTemplateService,
    private eventService: EventService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private confirmationModal: ConfirmationModalService,
  ) {
    super();
  }

  protected readonly EditIcon = Edit;
  protected readonly Trash2Icon = Trash2;
  protected readonly PlusIcon = Plus;
  protected readonly CalendarIcon = Calendar;
  protected readonly ClockIcon = Clock;
  protected readonly UsersIcon = Users;
  protected readonly BuildingIcon = Building;

  template: EventTemplateModel | null = null;
  loading = false;
  deleting = false;
  isRightSidebarOpen = false;
  rightSideBarContent?: string;
  applyingTemplate = false;
  protected isAdmin = false;
  protected canApplyTemplates = false;

  ngOnInit(): void {
    this.authService.getUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        const roles = user?.roles ?? [];
        this.isAdmin = roles.includes('admin');
        const isManager = roles.includes('manager');
        this.canApplyTemplates = this.isAdmin || isManager;
      },
      error: () => {
        this.isAdmin = false;
        this.canApplyTemplates = false;
      }
    });

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data['template']?.data) {
        this.template = data['template'].data;
      }
    });
  }

  getEventTypeColor(eventType: EventTemplateModel['event_type']): string {
    return EventTemplateModelUtils.getEventTypeColor(eventType);
  }

  formatDaysOffset(offset: number): string {
    return EventTemplateItemModelUtils.formatDaysOffset(offset);
  }

  formatItemOffset(item: EventTemplateItemModel): string {
    return EventTemplateItemModelUtils.formatOffsetDisplay(item.offset_amount, item.offset_unit, item.offset_direction);
  }

  formatTimeOfDay(timeString: string): string {
    return EventTemplateItemModelUtils.formatTimeOfDay(timeString);
  }

  getSortedItems(): EventTemplateItemModel[] {
    if (!this.template?.items) return [];

    return [...this.template.items].sort((a, b) => {
      // First sort by days_offset, then by time
      if (a.days_offset !== b.days_offset) {
        return a.days_offset - b.days_offset;
      }
      return a.time_of_day.localeCompare(b.time_of_day);
    });
  }

  getEventStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'Opportunity': 'secondary',
      'Provisional': 'warning',
      'Confirmed': 'success',
      'Cancelled': 'error',
      'Closed': 'primary'
    };
    return colorMap[status] || 'secondary';
  }

  editTemplate(): void {
    if (!this.isAdmin || !this.template?.id) return;
    this.router.navigate(['/event-templates', this.template.id, 'edit']);
  }

  async deleteTemplate(): Promise<void> {
    if (!this.isAdmin || !this.template?.id) return;

    const confirmed = await this.confirmationModal.confirm({
      title: 'Delete template',
      message: `Are you sure you want to delete the template "${this.template.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete Template',
      cancelLabel: 'Keep Template',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) return;

    this.deleting = true;
    this.eventTemplateService
      .deleteTemplate(this.template.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success(`Template "${this.template!.name}" has been deleted`);
          this.router.navigate(['/event-templates']);
        },
        error: (error) => {
          this.deleting = false;
          this.notificationService.error('Failed to delete template');
          console.error('Error deleting template:', error);
        },
      });
  }

  applyToEvent(): void {
    if (!this.canApplyTemplates) {
      return;
    }
    this.openRightSidebar('event-selection');
  }

  openRightSidebar(content: string): void {
    this.isRightSidebarOpen = true;
    this.rightSideBarContent = content;
  }

  closeRightSidebar(): void {
    this.isRightSidebarOpen = false;
    this.rightSideBarContent = undefined;
  }

  onEventSelected(event: EventModel): void {
    if (!this.template?.id) return;

    this.applyingTemplate = true;
    this.closeRightSidebar();

    // Apply template to the selected event using the dedicated endpoint
    this.eventTemplateService.applyToEvent(this.template.id, event.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applyingTemplate = false;
          this.notificationService.success(
            `Template "${this.template!.name}" applied to event "${event.name}" successfully!`
          );
        },
        error: (error) => {
          this.applyingTemplate = false;
          this.notificationService.error('Failed to apply template to event');
          console.error('Error applying template:', error);
        }
      });
  }

  duplicateTemplate(): void {
    if (!this.isAdmin || !this.template) return;

    const duplicatedTemplate: EventTemplateModel = {
      ...this.template,
      id: null,
      name: `${this.template.name} (Copy)`,
      created_at: null,
      updated_at: null,
      items: this.template.items?.map((item) => ({
        ...item,
        id: null,
        template_id: 0,
        created_at: null,
        updated_at: null,
      })),
    };

    this.eventTemplateService
      .saveWithItems(duplicatedTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.notificationService.success('Template duplicated successfully');
          this.router.navigate(['/event-templates', response.data.id]);
        },
        error: (error) => {
          this.notificationService.error('Failed to duplicate template');
          console.error('Error duplicating template:', error);
        },
      });
  }

}
